import { DEALERS_API_URL, MOBILITY_API_URL, SELF_SERVICE_API_URL, SELF_SERVICE_MODEL_URL, SELF_SERVICE_TOKEN_KIOSK_URL } from 'constants/ApiConstants';
import * as actionTypes from 'modules/selfServices/actionTypes';

/**
 * @param {import('./types').GetModelPayload} params
 * @param {Object<string, any>=} meta
 * @returns {import('./types').GetModelAction}
 */
export const fetchModel = ({ dms, ...params }, meta = {}) => ({
  type: actionTypes.FETCH_MODEL,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_MODEL_URL,
      params,
    },
    dms,
  },
  meta,
});

/**
 * @param {import('./types').SelfService} model
 * @param {Object<string, any>=} meta
 * @returns {import('./types').GetModelSuccessAction}
 */
export const fetchModelSuccess = (model, meta = {}) => ({
  type: actionTypes.FETCH_MODEL_SUCCESS,
  payload: model,
  meta,
});

/**
 * @param {string} error
 * @param {Object<string, any>=} meta
 * @returns {import('./types').GetModelFailureAction}
 */
export const fetchModelFailure = (error, meta = {}) => ({
  type: actionTypes.FETCH_MODEL_FAILURE,
  error: true,
  payload: error,
  meta,
});

/**
 * @param {string} id
 * @param {string} fileName
 * @param {{ origin: string }} params
 * @returns {import('./types').GetSummaryPdfAction}
 */
export const getSummaryPdf = (id, fileName, params) => ({
  type: actionTypes.FETCH_SUMMARY_PDF,
  payload: {
    request: {
      method: 'GET',
      url: `${SELF_SERVICE_API_URL}/${id}/pdf`,
      params,
    },
    fileName,
  },
});

/**
 * @param {string} message
 * @returns {import('./types').GetSummaryPdfFailureAction}
 */
export const getSummaryPdfFailure = message => ({
  type: actionTypes.FETCH_SUMMARY_PDF_FAILURE,
  payload: { message },
  error: true,
});

/**
 * @returns {import('./types').GetSummaryPdfSuccessAction}
 */
export const getSummaryPdfSuccess = () => ({
  type: actionTypes.FETCH_SUMMARY_PDF_SUCCESS,
});

/**
 * @param {string} selfServiceId
 * @param {Array<import('modules/global/typing/enums/messages.enum').MessageChannelEnum>} channels
 * @param {import('./types').SelfServiceTypeEnum} type
 * @param {boolean} ignoreRights
 * @param {Object={}} meta
 * @returns {import('./types').SendSelfServiceRequestAction}
 */
export const sendSelfService = (selfServiceId, channels, type, ignoreRights, meta = {}) => ({
  type: actionTypes.SEND_SELF_SERVICE,
  payload: {
    request: {
      method: 'POST',
      url: `${SELF_SERVICE_API_URL}/${selfServiceId}/send`,
      data: { channels, force: ignoreRights },
    },
    type,
  },
  meta,
});

/**
 * @param {Object} response
 * @param {Object={}} meta
 * @returns {import('./types').SendSelfServiceSuccessAction}
 */
export const sendSelfServiceSuccess = (response, meta = {}) => ({
  type: actionTypes.SEND_SELF_SERVICE_SUCCESS,
  payload: response,
  meta,
});

/**
 * @param {string} error
 * @param {Object={}} meta
 * @returns {import('./types').SendSelfServiceFailureAction}
 */
export const sendSelfServiceFailure = (error, meta = {}) => ({
  type: actionTypes.SEND_SELF_SERVICE_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} interventionId
 * @param {Object={}} meta
 * @returns {import('./types').OpenSendWizardAction}
 */
export const openSendWizard = (interventionId, type, meta = {}) => ({
  type: actionTypes.OPEN_SEND_WIZARD,
  payload: { interventionId, type },
  meta,
});

/**
 * @param {unknown} payload
 * @param {Object={}} meta
 * @returns {import('./types').CloseSendWizardAction}
 */
export const closeSendWizard = (payload, meta = {}) => ({
  type: actionTypes.CLOSE_SEND_WIZARD,
  payload,
  meta,
});

/**
 * @param {import('./types').SelfService=} payload
 * @param {Object={}} meta
 * @returns {import('./types').IsSendWizardReadyAction}
 */
export const isSendWizardReady = (payload, meta = {}) => ({
  type: actionTypes.SEND_CHECKOUT_READY,
  payload,
  meta,
});

/**
 * @param {import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention} intervention
 * @param {import('modules/global/typing/enums/messages.enum').MessageChannelEnum[]} channels
 * @param {import('./types').SelfServiceTypeEnum} type
 * @param {boolean} ignoreRights
 * @param {Object={}} meta
 * @returns {import('./types').SaveInterventionAndSendSelfServiceAction}
 */
export const saveInterventionAndSendSelfService = (intervention, channels, type, ignoreRights, meta = {}) => ({
  type: actionTypes.SAVE_INTERVENTION_AND_SEND_SELF_SERVICE,
  payload: { intervention, channels, type, ignoreRights },
  meta,
});

/**
 * @param {string} dealerId
 * @returns {import('./types').GetOnSiteLinkAction}
 */
export const getOnSiteLink = (dealerId, params, meta = {}) => ({
  type: actionTypes.GET_ON_SITE_LINK,
  payload: {
    request: {
      method: 'GET',
      url: `${DEALERS_API_URL}/${dealerId}/config/so/onSite/url`,
      params,
    },
  },
  meta,
});

/**
 * @param {import('./types').OnSiteLink} response
 * @param {Object={}} meta
 * @returns {import('./types').GetOnSiteLinkSuccessAction}
 */
export const getOnSiteLinkSuccess = (response, meta = {}) => ({
  type: actionTypes.GET_ON_SITE_LINK_SUCCESS,
  payload: response,
  meta,
});

/**
 * @param {string} error
 * @param {Object={}} meta
 * @returns {import('./types').GetOnSiteLinkFailureAction}
 */
export const getOnSiteLinkFailure = (error, meta = {}) => ({
  type: actionTypes.GET_ON_SITE_LINK_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} mobilityId
 * @param {Object={}} meta
 * @returns {import('./types').DeleteMobilityAction}
 */
export const deleteMobility = (mobilityId, meta = {}) => ({
  type: actionTypes.DELETE_MOBILITY,
  payload: {
    request: {
      method: 'DELETE',
      url: `${MOBILITY_API_URL}/${mobilityId}`,
    },
    mobilityId,
  },
  meta,
});

/**
 * @param {string} mobilityId
 * @param {Object={}} meta
 * @returns {import('./types').DeleteMobilitySuccessAction}
 */
export const deleteMobilitySuccess = (mobilityId, meta = {}) => ({
  type: actionTypes.DELETE_MOBILITY_SUCCESS,
  payload: mobilityId,
  meta,
});

/**
 * @param {string} error
 * @param {Object={}} meta
 * @returns {import('./types').DeleteMobilityFailureAction}
 */
export const deleteMobilityFailure = (error, meta = {}) => ({
  type: actionTypes.DELETE_MOBILITY_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {object} [meta={}]
 * @return {import('./types/actions').GetRequest}
 */
export const getKioskAdminToken = (meta = {}) => ({
  type: actionTypes.GET_KIOSK_ADMIN_TOKEN,
  payload: {
    request: {
      method: 'GET',
      url: SELF_SERVICE_TOKEN_KIOSK_URL,
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});
